import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import AnnotationCanvas from "./AnnotationCanvas";
import { Annotation, Point } from "../types/annotation.types";
import html2canvas from "html2canvas";
import useAppState from "../context/useAppState";
import { updateAnnotatedImage } from "../services/project";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AnnotationCanvas2 from "./AnnotationCanvas2";

interface AnnotationEditorProps {
  onAnnotationsChange?: (annotations: Annotation[]) => void;
  className?: string;
}

const AnnotationEditor = forwardRef<any, AnnotationEditorProps>(
  ({ onAnnotationsChange, className = "" }, ref) => {
    const annotationContainerRef = useRef<any>(null);
    const imageRef = useRef<any>(null);
    const canvasRef = useRef<any>(null);
    const [imageURL, setImageURL] = useState<string>("");
    const [s3PreSignedUrl, setS3PreSignedUrl] = useState<any>("");
    const [annotations, setAnnotations] = useState<any[]>([]);
    const [tempPoints, setTempPoints] = useState<{
      start?: Point;
      end?: Point;
    }>({});
    const [label, setLabel] = useState<string>("");
    const [imageScale, setImageScale] = useState<number>(1);
    const [annotationColor, setAnnotationColor] = useState<string>("#FF0000");
    // Color the annotations were last saved with — what Reset restores to
    const initialColorRef = useRef<string>("#FF0000");

    const { id, vijilizeId } = useParams();
    const navigate = useNavigate();
    const [isNewFormat, setIsNewFormat] = useState(false)

    const {
      imageCoordinates,
      annotationS3Path,
      markdownS3Path,
      markdownContent,
      setMarkdownContent,
      annotationData,
      patentImage,
      fontInfo
    } = useAppState("vijilize");

    console.log({ annotationData })

    const legendSuggestions = useMemo(() => {
      if (
        !patentImage ||
        !patentImage.figure_legend ||
        !Array.isArray(patentImage.figure_legend)
      ) {
        return [];
      }

      return patentImage.figure_legend
        .map((legend: any) => {
          const key = Object.keys(legend)[0];
          return legend[key];
        })
        .filter((value: string) => value && value !== "could not locate");
    }, [patentImage]);

    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>(
      []
    );
    const inputRef = useRef<HTMLInputElement>(null);

    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const [initialAnnotations, setInitialAnnotations] = useState<Annotation[]>(
      []
    );

    useImperativeHandle(ref, () => ({
      handleUpdateAnnotatedImage,
    }));
    useEffect(() => {
      if (annotationS3Path) {
        const httpUrl = annotationS3Path.startsWith("s3://")
          ? convertS3ToHttpUrl(annotationS3Path)
          : annotationS3Path;

        setS3PreSignedUrl(httpUrl);

        if (httpUrl) {
          setImageURL(httpUrl);

          console.log({ imageCoordinates, fontInfo })

          if (imageCoordinates && imageCoordinates.length > 0) {
            let firstElement = imageCoordinates[0];

            // Restore the color the annotations were saved with (default red)
            const storedColor = imageCoordinates.find(
              (it: any) => (it?.is_active ?? true) && it?.color
            )?.color;
            if (storedColor) {
              setAnnotationColor(storedColor);
              initialColorRef.current = storedColor;
            }

            if (firstElement.hasOwnProperty('arrow_start_coordinate') && firstElement.hasOwnProperty('arrow_end_coordinate')) {
              // let updatedImageCoOrdinates = imageCoordinates.map((item: any) => ({...item, is_active: true}))
              const mappedAnnotations: any[] = [];

              imageCoordinates.forEach((item: any, index: number) => {
                const isActive = item?.is_active ?? true;
                if (!isActive) return;

                if (item.arrow_start_coordinate && item.arrow_end_coordinate) {
                  mappedAnnotations.push({ ...item, is_active: true });
                }
                else if (Array.isArray(item.point)) {
                  const [yNorm, xNorm] = item.point;

                  const angleOffset = ((index * Math.PI) / 4) % (2 * Math.PI);
                  const distance = 80;
                  const endX = xNorm + distance * Math.cos(angleOffset);
                  const endY = yNorm + distance * Math.sin(angleOffset);

                  mappedAnnotations.push({
                    start: { x: xNorm, y: yNorm },
                    end: { x: endX, y: endY },
                    text: item?.label || item?.text,
                    is_active: true
                  });
                }
              });

              console.log({ mappedAnnotations })

              setAnnotations(mappedAnnotations)
              // Deep copy so drags on the live annotations can never touch the originals
              setInitialAnnotations(JSON.parse(JSON.stringify(mappedAnnotations)));
              setIsNewFormat(true)
            } else {
              setIsNewFormat(false)

              // const mappedAnnotations: Annotation[] = [];
              const mappedAnnotations: any[] = [];

              imageCoordinates.forEach((item: any, index: number) => {
                if (!item?.is_active) return;

                const [yNorm, xNorm] = item.point;

                const angleOffset = ((index * Math.PI) / 4) % (2 * Math.PI);
                const distance = 80;
                const endX = xNorm + distance * Math.cos(angleOffset);
                const endY = yNorm + distance * Math.sin(angleOffset);

                mappedAnnotations.push({
                  start: { x: xNorm, y: yNorm },
                  end: { x: endX, y: endY },
                  text: item.label,
                });
              });

              console.log({ mappedAnnotations })

              setAnnotations(mappedAnnotations);
              setInitialAnnotations(JSON.parse(JSON.stringify(mappedAnnotations)));
            }
          }
        }
      } else {
        const timer = setTimeout(() => {
          navigate(-1);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [annotationS3Path, imageCoordinates, navigate]);

    useEffect(() => {
      const timer = setTimeout(() => {
        if (!imageURL && annotationS3Path === undefined) {
          navigate(-1);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }, [imageURL, navigate, annotationS3Path]);

    const convertS3ToHttpUrl = (s3Path: string): string => {
      if (s3Path.startsWith("s3://")) {
        const path = s3Path.substring(5);
        const slashIndex = path.indexOf("/");
        const bucket = path.substring(0, slashIndex);
        const key = path.substring(slashIndex + 1);
        return `https://${bucket}.s3.amazonaws.com/${key}`;
      }
      return s3Path;
    };

    const handleImageUpload = (file: File) => {
      const url = URL.createObjectURL(file);
      setImageURL(url);
      setAnnotations([]);
      setTempPoints({});
    };

    const handleAddPoint = (point: any) => {
      if (!tempPoints.start) {
        setTempPoints({ start: point });
      } else if (!tempPoints.end) {
        setTempPoints({ ...tempPoints, end: point });
      }
    };

    // const handleAddAnnotation = () => {
    //   if (tempPoints.start && tempPoints.end && label) {
    //     const newAnnotations = [
    //       ...annotations,
    //       !isNewFormat
    //         ? { start: tempPoints.start, end: tempPoints.end, text: label }
    //         : { isManual: true, label: label, is_active: true, score: '0.95', arrow_end_coordinate: [tempPoints.start.x, tempPoints.start.y], arrow_start_coordinate: [tempPoints.end.x, tempPoints.end.y], point: [tempPoints.start.x, tempPoints.start.y] }
    //     ];

    //     setAnnotations(newAnnotations);
    //     onAnnotationsChange?.(newAnnotations);
    //     setTempPoints({});
    //     setLabel("");
    //     setShowSuggestions(false);
    //   }
    // };

    const handleAddAnnotation = () => {
      if (tempPoints.start && tempPoints.end && label) {
        let newAnnotations: any = [];

        if (isNewFormat) {


          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          const dx = tempPoints.end.x - tempPoints.start.x;
          const dy = tempPoints.end.y - tempPoints.start.y;
          const length = Math.hypot(dx, dy);
          const offset = 20;

          let labelX = tempPoints.end.x + (dx / length) * offset;
          let labelY = tempPoints.end.y + (dy / length) * offset;

          // Clamp label inside canvas (1000x1000 assumed)
          labelX = Math.max(0, Math.min(1000, labelX));
          labelY = Math.max(0, Math.min(1000, labelY));

          const scaledTextAnchor = {
            x: labelX,
            y: labelY,
          };

          const { text_start_coordinate, text_end_coordinate } = computeTextBoundingBox(
            ctx,
            label,
            scaledTextAnchor
          );

          newAnnotations = [
            ...annotations,
            {
              isManual: true,
              label,
              is_active: true,
              score: "0.95",
              arrow_end_coordinate: [tempPoints.start.x, tempPoints.start.y],
              arrow_start_coordinate: [tempPoints.end.x, tempPoints.end.y],
              point: [tempPoints.start.x, tempPoints.start.y],
              text_start_coordinate,
              text_end_coordinate,
            },
          ];
        } else {
          newAnnotations = [
            ...annotations,
            { start: tempPoints.start, end: tempPoints.end, text: label }
          ];
        }

        setAnnotations(newAnnotations);
        onAnnotationsChange?.(newAnnotations);
        setTempPoints({});
        setLabel("");
        setShowSuggestions(false);
      }
    };

    const computeTextBoundingBox = (
      ctx: CanvasRenderingContext2D,
      text: string,
      start: { x: number, y: number },
      fontSize: number = 16,
      lineHeight: number = 20,
      padding: number = 6,
      maxWidth: number = 220
    ) => {
      // ctx.font = `${fontSize}px ${fontName}`;
      ctx.textBaseline = "top";

      const words = text.split(" ");
      let line = "";
      const lines: string[] = [];

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " ";
        if (ctx.measureText(testLine).width > maxWidth && i > 0) {
          lines.push(line.trim());
          line = words[i] + " ";
        } else {
          line = testLine;
        }
      }
      lines.push(line.trim());

      const boxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 2 * padding;
      const boxHeight = lines.length * lineHeight + 2 * padding;

      return {
        text_start_coordinate: [start.x, start.y],
        text_end_coordinate: [start.x + boxWidth, start.y + boxHeight]
      };
    };


    const handleAddSuggestedAnnotation = (item: {
      point: [number, number];
      label: string;
      is_active: boolean;
    }) => {
      const [yNorm, xNorm] = item.point;

      const index = annotations.length;
      const angleOffset = ((index * Math.PI) / 4) % (2 * Math.PI);
      const distance = 80;
      const endX = xNorm + distance * Math.cos(angleOffset);
      const endY = yNorm + distance * Math.sin(angleOffset);

      const newAnnotation = {
        start: { x: xNorm, y: yNorm },
        end: { x: endX, y: endY },
        text: item.label,
      };

      const newAnnotations = [...annotations, newAnnotation];
      setAnnotations(newAnnotations);
      onAnnotationsChange?.(newAnnotations);
    };

    const handleClearCurrent = () => {
      setTempPoints({});
      setLabel("");
    };

    const handleClearAll = () => {
      setAnnotations([]);
      onAnnotationsChange?.([]);
      setTempPoints({});
      setLabel("");
    };

    const handleResetAnnotations = () => {
      const restored = JSON.parse(JSON.stringify(initialAnnotations));
      setAnnotations(restored);
      setTempPoints({});
      setAnnotationColor(initialColorRef.current);
      if (onAnnotationsChange) onAnnotationsChange(restored);
    };

    const handleColorChange = (color: string) => {
      setAnnotationColor(color);
      // Color is baked into the saved image, so surface the update button
      if (annotations.length) onAnnotationsChange?.(annotations);
    };
    const areAnnotationsChanged = () => {
      if (annotations.length !== initialAnnotations.length) return true;

      return annotations.some((ann, index) => {
        const initAnn = initialAnnotations[index];
        return (
          !initAnn ||
          ann.text !== initAnn.text ||
          ann.start.x !== initAnn.start.x ||
          ann.start.y !== initAnn.start.y ||
          ann.end.x !== initAnn.end.x ||
          ann.end.y !== initAnn.end.y
        );
      });
    };

    const handleDeleteAnnotation = (index: number) => {
      const newAnnotations = annotations.filter((_, i) => i !== index);
      setAnnotations(newAnnotations);
      onAnnotationsChange?.(newAnnotations);
    };

    // Live-updates one annotation while its text is dragged; notifies on drop.
    const handleUpdateAnnotation = (index: number, updated: any, commit?: boolean) => {
      const newAnnotations = annotations.map((a, i) => (i === index ? updated : a));
      setAnnotations(newAnnotations);
      if (commit) onAnnotationsChange?.(newAnnotations);
    };

    const suggestedAnnotations = useMemo(() => {
      if (!imageCoordinates || imageCoordinates.length === 0) return [];

      const activeCoordinates = imageCoordinates.filter(
        (item: any) => item?.is_active
      );

      return activeCoordinates.filter((item: any) => {
        const [yNorm, xNorm] = item?.point || [];
        return !annotations.some(
          (ann) =>
            Math.abs(ann?.start?.x - xNorm) < 0.001 &&
            Math.abs(ann?.start?.y - yNorm) < 0.001 &&
            ann?.text === item?.label
        );
      });
    }, [imageCoordinates, annotations]);

    const handleDownloadAnnotatedImage = async () => {
      if (!annotationContainerRef.current || !imageRef.current) return;

      try {
        const imgWidth = imageRef.current.naturalWidth;
        const imgHeight = imageRef.current.naturalHeight;

        const canvas = await html2canvas(annotationContainerRef.current, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
          width: imgWidth * imageScale,
          height: imgHeight * imageScale,
        });

        const link = document.createElement("a");
        link.download = `annotated-image-${new Date()
          .toISOString()
          .replace(/[:.]/g, "_")}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      } catch (error) {
        console.error("Error downloading annotated image:", error);
      }
    };

    const handleUpdateAnnotatedImage = async () => {
      if (!annotationContainerRef.current || !imageRef.current) {
        toast.error(
          "Cannot update image: No annotation container or image reference found"
        );
        return;
      }

      if (!markdownS3Path || !annotationS3Path) {
        toast.error("Cannot update image: Missing required S3 paths");
        return;
      }

      if (!isNewFormat) {
        try {
          setIsUpdating(true);

          const initialCoordinates = imageCoordinates
            ? imageCoordinates.map(
              (item: { point: [number, number]; label: string }) => ({
                point: item.point,
                label: item.label,
                is_active: false,
              })
            )
            : [];

          const currentAnnotations = annotations.map((annotation) => ({
            point: [annotation.start.y, annotation.start.x],
            label: annotation?.text || annotation?.label,
            is_active: true,
            color: annotationColor,
          }));

          const uniqueAnnotations = new Map();

          [...initialCoordinates, ...currentAnnotations].forEach((annotation) => {
            const key = `${JSON.stringify(annotation.point)}-${annotation.label}`;

            if (
              !uniqueAnnotations.has(key) ||
              (!uniqueAnnotations.get(key).is_active && annotation.is_active)
            ) {
              uniqueAnnotations.set(key, annotation);
            }
          });

          const imageAnnotations = Array.from(uniqueAnnotations.values());

          const imgWidth = imageRef.current.naturalWidth;
          const imgHeight = imageRef.current.naturalHeight;

          const canvas = await html2canvas(annotationContainerRef?.current, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,
            width: imgWidth * imageScale,
            height: imgHeight * imageScale,
          });

          const annotatedImageData = canvas.toDataURL("image/png");

          let selectedTeam: any = JSON.parse(
            localStorage.getItem("selectedTeam") as string
          );
          let team_id: string = selectedTeam.currentTeamId;

          console.log({ imageAnnotations })
          
          // Get annotationData from sessionStorage if not in context
          let finalAnnotationData = annotationData;
          if (!finalAnnotationData) {
            const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
            if (claimDataStr) {
              try {
                const claimData = JSON.parse(claimDataStr);
                finalAnnotationData = {
                  annotationId: claimData.annotationId,
                  patentId: claimData.activeIndex?.patentId,
                  claimKey: claimData.storedClaimKey || `claim${claimData.activeIndex?.claimIndex + 1}`,
                  isMatchedItem: claimData.isMatchedItem,
                };
                console.log("Retrieved annotationData from sessionStorage:", finalAnnotationData);
              } catch (e) {
                console.error("Error parsing vijilizeClaimData:", e);
              }
            }
          }
          
          console.log("DEBUG - annotationData:", finalAnnotationData);
          console.log("DEBUG - vijilizeId:", vijilizeId);

          const response: any = await updateAnnotatedImage(
            vijilizeId,
            markdownS3Path,
            annotationS3Path,
            annotatedImageData,
            id,
            team_id,
            imageAnnotations,
            finalAnnotationData
          );

          console.log("DEBUG - Response:", response);

          if (response?.success && response?.data) {
            toast.success("Annotated image updated successfully");
            const newMarkdownS3FileUrl = response.data.newMarkdownS3FileUrl;

            if (markdownContent && newMarkdownS3FileUrl) {
              const imageUrlPattern = /!\[.*?\]\((.*?)\)/g;
              let updatedContent = markdownContent;

              const matches: any = markdownContent.matchAll(imageUrlPattern);
              for (const match of Array.from(matches) as any) {
                const oldUrl = match[1] as string;
                if (oldUrl.includes(annotationS3Path.split("/").pop() || "")) {
                  updatedContent = updatedContent.replace(
                    oldUrl,
                    newMarkdownS3FileUrl
                  );
                  break;
                }
              }

              setMarkdownContent(updatedContent);
            }

            setTimeout(() => {
              navigate(-1);
            }, 1000);
          } else {
            toast.error("Failed to update annotated image");
          }
        } catch (error) {
          console.error("Error updating annotated image:", error);
          toast.error("Error updating annotated image");
        } finally {
          setIsUpdating(false);
        }
      } else {
        try {
          setIsUpdating(true);

          const initialCoordinates = imageCoordinates
            ? imageCoordinates.map(
              (item: any) => ({
                ...item,
                is_active: false,
              })
            )
            : [];

          const currentAnnotations = annotations.map(annotation => {
            if (annotation?.start && annotation?.end) {
              return {
                point: [annotation.start.x, annotation.start.y],
                label: annotation.text || annotation.label,
                is_active: true,
                arrow_end_coordinate: [annotation.start.x, annotation.start.y],
                arrow_start_coordinate: [annotation.end.x, annotation.end.y],
                isManual: true,
                score: '0.95',
                color: annotationColor
              };
            } else {
              return { ...annotation, color: annotationColor };
            }
          });

          const uniqueAnnotations = new Map();

          [...initialCoordinates, ...currentAnnotations].forEach((annotation) => {
            const key = `${JSON.stringify(annotation.point)}-${annotation.label}`;

            if (
              !uniqueAnnotations.has(key) ||
              (!uniqueAnnotations.get(key).is_active && annotation.is_active)
            ) {
              uniqueAnnotations.set(key, annotation);
            }
          });

          const imageAnnotations = Array.from(uniqueAnnotations.values());

          const imgWidth = imageRef.current.naturalWidth;
          const imgHeight = imageRef.current.naturalHeight;

          const canvas = await html2canvas(annotationContainerRef?.current, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,
            width: imgWidth * imageScale,
            height: imgHeight * imageScale,
          });

          const annotatedImageData = canvas.toDataURL("image/png");

          let selectedTeam: any = JSON.parse(
            localStorage.getItem("selectedTeam") as string
          );
          let team_id: string = selectedTeam.currentTeamId;

          console.log({ imageAnnotations })
          
          // Get annotationData from sessionStorage if not in context
          let finalAnnotationData = annotationData;
          if (!finalAnnotationData) {
            const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
            if (claimDataStr) {
              try {
                const claimData = JSON.parse(claimDataStr);
                finalAnnotationData = {
                  annotationId: claimData.annotationId,
                  patentId: claimData.activeIndex?.patentId,
                  claimKey: claimData.storedClaimKey || `claim${claimData.activeIndex?.claimIndex + 1}`,
                  isMatchedItem: claimData.isMatchedItem,
                };
                console.log("Retrieved annotationData from sessionStorage:", finalAnnotationData);
              } catch (e) {
                console.error("Error parsing vijilizeClaimData:", e);
              }
            }
          }
          
          console.log("DEBUG - annotationData:", finalAnnotationData);
          console.log("DEBUG - vijilizeId:", vijilizeId);

          const response: any = await updateAnnotatedImage(
            vijilizeId,
            markdownS3Path,
            annotationS3Path,
            annotatedImageData,
            id,
            team_id,
            imageAnnotations,
            finalAnnotationData
          );

          console.log("DEBUG - Response:", response);

          if (response?.success && response?.data) {
            toast.success("Annotated image updated successfully");
            const newMarkdownS3FileUrl = response.data.newMarkdownS3FileUrl;

            if (markdownContent && newMarkdownS3FileUrl) {
              const imageUrlPattern = /!\[.*?\]\((.*?)\)/g;
              let updatedContent = markdownContent;

              const matches: any = markdownContent.matchAll(imageUrlPattern);
              for (const match of Array.from(matches) as any) {
                const oldUrl = match[1] as string;
                if (oldUrl.includes(annotationS3Path.split("/").pop() || "")) {
                  updatedContent = updatedContent.replace(
                    oldUrl,
                    newMarkdownS3FileUrl
                  );
                  break;
                }
              }

              setMarkdownContent(updatedContent);
            }

            setTimeout(() => {
              navigate(-1);
            }, 1000);
          } else {
            toast.error("Failed to update annotated image");
          }
        } catch (error) {
          console.error("Error updating annotated image:", error);
          toast.error("Error updating annotated image");
        } finally {
          setIsUpdating(false);
        }
      }
    };

    return (
      <div className={`flex flex-col md:flex-row h-full ${className}`}>
        {/* Sidebar */}
        <div className="w-full md:w-80 bg-gray-50 p-4 border-r border-gray-200 overflow-y-auto">
          {/* Image size control slider */}
          {imageURL && (
            <div className="mb-4 pb-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Image Size: {Math.round(imageScale * 100)}%
                </span>
                <button
                  onClick={() => {
                    setImageScale(1);
                    handleResetAnnotations();
                  }}
                  className="px-2 py-0.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                  title="Reset zoom and annotations"
                >
                  Reset
                </button>
              </div>
              <input
                id="image-size-slider"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={imageScale}
                onChange={(e) => setImageScale(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-blue-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          )}

          {imageURL && (
            <div className="mb-4 pb-3 border-b border-gray-200">
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Annotation Color
              </span>
              <div className="flex items-center gap-2">
                {["#FF0000", "#1751D0", "#047857", "#D97706", "#000000"].map(
                  (c) => (
                    <button
                      key={c}
                      onClick={() => handleColorChange(c)}
                      style={{ backgroundColor: c }}
                      className={`w-6 h-6 rounded-full border-2 ${
                        annotationColor.toUpperCase() === c
                          ? "border-gray-700 ring-2 ring-offset-1 ring-gray-400"
                          : "border-white shadow"
                      }`}
                      title={c}
                    />
                  )
                )}
                <input
                  type="color"
                  value={annotationColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-7 h-7 p-0 border border-gray-300 rounded cursor-pointer"
                  title="Custom color"
                />
              </div>
            </div>
          )}

          {tempPoints.start && tempPoints.end && (
            <div>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Enter label..."
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);

                    if (e.target.value.trim() === "") {
                      setShowSuggestions(false);
                      return;
                    }

                    const filtered = legendSuggestions.filter(
                      (suggestion: string) =>
                        suggestion
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase())
                    );

                    setFilteredSuggestions(filtered);
                    setShowSuggestions(filtered.length > 0);
                  }}
                  onFocus={() => {
                    // Show all legend suggestions when input is focused
                    setFilteredSuggestions(legendSuggestions);
                    setShowSuggestions(legendSuggestions.length > 0);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSuggestions(false);
                    }, 200);
                  }}
                  className="w-full p-2.5 mb-2 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredSuggestions.map(
                      (suggestion: string, index: number) => (
                        <div
                          key={index}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
                          onMouseDown={() => {
                            setLabel(suggestion);
                            setShowSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddAnnotation}
                className="w-full py-2 mb-2 rounded bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
              >
                Add Annotation
              </button>
            </div>
          )}
          <button
            onClick={handleClearCurrent}
            disabled={!tempPoints.start}
            className="w-full py-2 mb-2 rounded bg-gray-600 text-white font-medium transition disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Delete Annotation
          </button>
          <button
            onClick={handleClearAll}
            disabled={annotations.length === 0}
            className="w-full py-2 mb-2 rounded bg-blue-600 text-white font-medium transition disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Clear All Annotations
          </button>
          <button
            onClick={handleResetAnnotations}
            disabled={initialAnnotations.length === 0}
            className={`w-full py-2 mb-2 rounded bg-yellow-600 text-white font-medium transition disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed ${initialAnnotations.length === 0 ? "button-disabled" : ""
              }  `}
          >
            Reset Annotations
          </button>
          <hr className="my-4" />
          <h4 className="mb-2 text-gray-700 font-semibold">Annotations</h4>
          <div className="max-h-[300px] overflow-y-auto mt-4 space-y-2">
            {annotations.map((ann, i) => (
              <div
                key={i}
                className="flex items-center mb-2 bg-gray-50 rounded px-2 py-1"
              >
                <span
                  className="flex-1 truncate text-gray-800"
                  title={ann?.text || ann?.label}
                >
                  {ann.text || ann?.label}
                </span>
                <button
                  onClick={() => handleDeleteAnnotation(i)}
                  className="ml-2 px-2 py-1 text-red-600 hover:bg-red-100 rounded transition"
                  title="Delete annotation"
                >
                  ✖
                </button>
              </div>
            ))}

            {suggestedAnnotations.length > 0 && (
              <div className="mt-4">
                {suggestedAnnotations.map(
                  (
                    item: {
                      point: [number, number];
                      label: string;
                      is_active: boolean;
                    },
                    i: number
                  ) => (
                    <div
                      key={`suggestion-${i}`}
                      className="flex items-center mb-2 bg-blue-50 border border-blue-100 rounded px-2 py-1"
                    >
                      <span
                        className="flex-1 truncate text-blue-800"
                        title={item.label}
                      >
                        {item.label}
                      </span>
                      <button
                        onClick={() => handleAddSuggestedAnnotation(item)}
                        className="ml-2 px-2 py-1 text-blue-600 hover:bg-blue-100 rounded transition"
                        title="Add suggested annotation"
                      >
                        ✚
                      </button>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 p-2 md:p-3 bg-white flex flex-col items-start">
          {imageURL && (
            <div className="w-full">
              <div
                ref={annotationContainerRef}
                className="relative max-w-full overflow-auto"
                style={{
                  maxHeight: "100vh",
                }} /* Add scrolling for large scaled images */
              >
                {
                  isNewFormat &&
                  <AnnotationCanvas2
                    imageSrc={imageURL}
                    annotations={annotations}
                    onAddPoint={handleAddPoint}
                    tempPoints={tempPoints}
                    imageRef={imageRef}
                    canvasRef={canvasRef}
                    imageScale={imageScale}
                    fontInfo={fontInfo}
                    onUpdateAnnotation={handleUpdateAnnotation}
                    color={annotationColor}
                  />
                }
                {
                  !isNewFormat &&
                  <AnnotationCanvas
                    imageSrc={imageURL}
                    annotations={annotations}
                    onAddPoint={handleAddPoint}
                    tempPoints={tempPoints}
                    imageRef={imageRef}
                    canvasRef={canvasRef}
                    imageScale={imageScale}
                    fontInfo={fontInfo}
                    onUpdateAnnotation={handleUpdateAnnotation}
                    color={annotationColor}
                  />
                }
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default AnnotationEditor;
